import Button from '@/Components/Button';
import { ArrowDownOnSquareIcon } from '@heroicons/react/24/outline';
import { TextField } from '@mui/material';
import { memo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import EmptyState from './EmptyState';
import ImageListItem from './ImageListItem';
import SkeletonCard from './SkeletonCard';

/**
 * Images list component with search and infinite scroll
 * @param {Object} props - Component props
 * @returns {JSX.Element} Images list
 */
const ImagesList = ({
  theme,
  files,
  fileSelected,
  selectedIndex,
  searchTerm,
  hasMore,
  noVision,
  onInputChange,
  onNext,
  onFileClick,
  onBadInteraction,
  onDeleteFile,
  onInteraction,
}) => {
  return (
    <>
      <div
        className={`${
          theme === 'dark' ? 'text-crystal-bell' : 'text-raisin-black'
        } text-base font-medium mb-2`}
      >
        Choose from your uploaded images
      </div>

      <TextField
        label="Search images"
        variant="outlined"
        value={searchTerm}
        onChange={onInputChange}
        fullWidth
        sx={{ mt: 1, mb: 2 }}
        InputProps={{
          sx: {
            height: '34px',
            fontSize: '14px',
            '& .MuiOutlinedInput-input': {
              padding: '12px 14px',
            },
            '& .MuiInputLabel-outlined': {
              transform: 'translate(14px, 10px) scale(1)',
            },
          },
        }}
        InputLabelProps={{
          sx: {
            fontSize: '14px',
            transform: 'translate(14px, 6px) scale(1)',
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -6px) scale(0.75)',
            },
          },
        }}
      />

      {files.length > 0 && (
        <>
          <InfiniteScroll
            dataLength={files.length}
            next={onNext}
            height={300}
            hasMore={hasMore}
            loader={
              <div className="flex gap-3 flex-col mt-3 w-full">
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <SkeletonCard key={index} />
                ))}
              </div>
            }
            endMessage={
              <p className="text-center mt-6 mb-3 text-raisin-black dark:text-crystal-bell">
                Congratulations, you've seen all your images!
              </p>
            }
            className={`overflow-rtl ${
              theme === 'dark' ? 'overflow-rtl-dark' : 'overflow-rtl-light'
            }`}
          >
            {files.map((file, index) => (
              <ImageListItem
                key={index}
                file={file}
                index={index}
                selectedIndex={selectedIndex}
                theme={theme}
                noVision={noVision}
                onFileClick={onFileClick}
                onBadInteraction={onBadInteraction}
                onDeleteFile={onDeleteFile}
              />
            ))}
          </InfiniteScroll>

          <div className="flex justify-end w-full mt-4">
            <Button
              Icon={ArrowDownOnSquareIcon}
              title="Insert"
              disabled={fileSelected.length <= 0}
              onClick={() => onInteraction(fileSelected)}
              iconSize="w-[18px] h-[18px]"
              customRound="rounded-xl"
              customPaddig="px-4 py-2"
              customWeight="font-semibold"
              noUppercase
            />
          </div>
        </>
      )}

      {files.length === 0 && !hasMore && <EmptyState />}
    </>
  );
};

export default memo(ImagesList);
